import { Fragment, useRef, useState } from 'react'

import { Dialog, Transition } from '@headlessui/react'
import MenuIcon from '@heroicons/react/outline/MenuIcon'
import XIcon from '@heroicons/react/outline/XIcon'
import { Link } from 'react-router-dom'

import { useAuthToken } from '@tribeplatform/react-sdk/hooks'

import { ErrorBoundary } from '../../Error/ErrorBoundry'
import { Logo } from '../../Logo'
import { ThemePreviewProvider } from '../Editor/ThemePreviewContext'
import { ScrollProvider } from '../ScrollProvider'
import { AdminSidebar } from './AdminSidebar'

export const AdminMenu = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { data: authToken } = useAuthToken()
  const { networkPublicInfo } = authToken || {}
  const containerRef = useRef<HTMLDivElement>()

  return (
    <>
      <div className="block lg:hidden">
        <Transition.Root show={sidebarOpen} as={Fragment}>
          <Dialog
            as="div"
            className="fixed inset-0 z-40 flex lg:hidden"
            onClose={setSidebarOpen}
          >
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-gray-600 bg-opacity-75" />
            </Transition.Child>
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <div className="relative max-w-md bg-main-50 flex-1 flex flex-col">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute top-0 right-0 -mr-14 p-1">
                    <button
                      type="button"
                      className="h-12 w-12 rounded-full flex items-center justify-center focus:outline-none focus:bg-gray-600"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <XIcon
                        className="h-6 w-6 text-white"
                        aria-hidden="true"
                      />
                      <span className="sr-only">Close sidebar</span>
                    </button>
                  </div>
                </Transition.Child>
                <div className="flex-shrink-0 flex">
                  <AdminSidebar onSelect={() => setSidebarOpen(false)} />
                </div>
              </div>
            </Transition.Child>
            <div className="flex-shrink-0 w-14" aria-hidden="true">
              {/* Dummy element to force sidebar to shrink to fit close icon */}
            </div>
          </Dialog>
        </Transition.Root>
        <div className="sticky top-0 z-10 px-3 flex-shrink-0 h-16 bg-actionSecondary-50 text-basicSecondary-500 shadow-sm flex items-center">
          <Link
            onClick={() => setSidebarOpen(true)}
            to="#"
            className="lg:hidden mr-3"
          >
            <MenuIcon className="h-8 w-8" aria-hidden="true" />
          </Link>
          <Link to="/">
            <Logo
              image={networkPublicInfo?.logo}
              alt={networkPublicInfo?.name}
            />
          </Link>
        </div>
      </div>
      <ThemePreviewProvider>
        <div className="flex min-h-screen bg-main-50 overflow-hidden">
          <div className="hidden lg:block">
            <AdminSidebar />
          </div>
          <div className="flex-grow overflow-hidden">
            <div className="h-screen overflow-auto" ref={containerRef}>
              <ScrollProvider scrollRef={containerRef}>
                <ErrorBoundary>{children}</ErrorBoundary>
              </ScrollProvider>
            </div>
          </div>
        </div>
      </ThemePreviewProvider>
    </>
  )
}
